import { forwardRef, useState, type ComponentPropsWithoutRef } from 'react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { MinusIcon, PlusIcon, UsersRoundIcon } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { getCribsLabel, getPersonsLabel } from '@/lib/utils'
import { useSearchFilters } from './useSearchFilters'

const BedsFilterTrigger = forwardRef<HTMLButtonElement, ComponentPropsWithoutRef<typeof Button>>(
  function BedsFilterTrigger(props, ref) {
    const { localFilters } = useSearchFilters()

    return (
      <Button
        ref={ref}
        variant="ghost"
        className="justify-start border-0 bg-slate-100 text-left text-sm! font-normal focus-visible:bg-slate-100 focus-visible:ring-1 focus-visible:ring-slate-200"
        {...props}
      >
        <UsersRoundIcon className="text-red h-4 w-4" />
        {localFilters.beds} {getPersonsLabel(localFilters.beds)}
        {localFilters.cribs > 0 && `, ${localFilters.cribs} ${getCribsLabel(localFilters.cribs)}`}
      </Button>
    )
  },
)

function BedsFilterContentMobile() {
  const { localFilters, updateLocalFilters } = useSearchFilters()

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-medium text-slate-800">Osoby</p>
          <p className="text-xs text-slate-400">dospělí i děti, každý s vlastním lůžkem</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="icon"
            className="flex h-8 w-8 items-center justify-center rounded-full"
            disabled={localFilters.beds <= 1}
            onClick={() => updateLocalFilters({ beds: Math.max(1, localFilters.beds - 1) })}
          >
            <MinusIcon className="h-4 w-4" />
          </Button>
          <p className="w-6 text-center text-xl font-medium text-slate-800">{localFilters.beds}</p>
          <Button
            variant="secondary"
            size="icon"
            className="flex h-8 w-8 items-center justify-center rounded-full"
            disabled={localFilters.beds >= 25}
            onClick={() => updateLocalFilters({ beds: Math.min(25, localFilters.beds + 1) })}
          >
            <PlusIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-medium text-slate-800">Dětské postýlky</p>
          <p className="text-xs text-slate-400">pro miminka, do počtu osob se nepočítají</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="icon"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200"
            disabled={localFilters.cribs <= 0}
            onClick={() => updateLocalFilters({ cribs: Math.max(0, localFilters.cribs - 1) })}
          >
            <MinusIcon className="h-4 w-4" />
          </Button>
          <p className="w-6 text-center text-xl font-medium text-slate-800">{localFilters.cribs}</p>
          <Button
            variant="secondary"
            size="icon"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200"
            disabled={localFilters.cribs >= 10}
            onClick={() =>
              updateLocalFilters({
                cribs: Math.min(10, localFilters.cribs + 1),
              })
            }
          >
            <PlusIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

function BedsFilterContentDesktop() {
  const { localFilters, updateLocalFilters } = useSearchFilters()

  return (
    <div className="grid gap-4 text-slate-800">
      <div className="grid grid-cols-[1fr_auto] items-center gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor="beds">Osoby</Label>
          <p className="text-xs text-slate-400">dospělí i děti, každý s vlastním lůžkem</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100"
            disabled={localFilters.beds <= 1}
            onClick={() => updateLocalFilters({ beds: Math.max(1, localFilters.beds - 1) })}
          >
            <MinusIcon className="h-4 w-4" />
          </Button>
          <p className="w-6 text-center">{localFilters.beds}</p>
          <Button
            variant="ghost"
            size="icon"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100"
            disabled={localFilters.beds >= 25}
            onClick={() => updateLocalFilters({ beds: Math.min(25, localFilters.beds + 1) })}
          >
            <PlusIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-[1fr_auto] items-center gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor="cribs">Dětské postýlky</Label>
          <p className="text-xs text-slate-400">pro miminka, do počtu osob se nepočítají</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100"
            disabled={localFilters.cribs <= 0}
            onClick={() => updateLocalFilters({ cribs: Math.max(0, localFilters.cribs - 1) })}
          >
            <MinusIcon className="h-4 w-4" />
          </Button>
          <p className="w-6 text-center">{localFilters.cribs}</p>
          <Button
            variant="ghost"
            size="icon"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100"
            disabled={localFilters.cribs >= 10}
            onClick={() =>
              updateLocalFilters({
                cribs: Math.min(10, localFilters.cribs + 1),
              })
            }
          >
            <PlusIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

export function BedsFilterMobile() {
  return <BedsFilterContentMobile />
}

export function BedsFilterDesktop() {
  const { commitFilters } = useSearchFilters()
  const [open, setOpen] = useState(false)

  const handleClose = () => {
    commitFilters()
    setOpen(false)
  }

  return (
    <div className="flex grow basis-0 flex-col gap-1">
      <label htmlFor="beds" className="text-sm text-slate-400">
        Počet osob
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <BedsFilterTrigger />
        </PopoverTrigger>
        <PopoverContent className="w-72" onInteractOutside={handleClose}>
          <div className="grid gap-4">
            <p className="text-base font-medium text-slate-800">Počet osob</p>
            <BedsFilterContentDesktop />
            <div className="flex justify-end">
              <Button size="sm" onClick={handleClose}>
                Potvrdit
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
